import React, { useEffect, useMemo, useState } from 'react'
import { or, rankWith, schemaMatches, uiTypeIs } from '@jsonforms/core'
import {
    useJsonForms,
    withJsonFormsControlProps,
    withTranslateProps,
} from '@jsonforms/react'
import { Unwrapped } from '@jsonforms/material-renderers'
import { Box } from '@mui/material'
import { useDynamicConfigs, usePreviewMode } from '../JsonFormsWrapper'
import { get } from 'lodash-es'
import { useNotify } from 'react-admin'

// Tells Jsonforms which control/component should be used.
export const DependentSelectControlTester = rankWith(
    Number.MAX_VALUE,
    // TODO As we expand support for configurable uischemas
    // Create a DependentSelectControl wrapper to wrap all dependent components
    or(
        uiTypeIs('DependentSelectControl'),
        schemaMatches(
            (schema) =>
                schema.hasOwnProperty('optionsMapPath') &&
                schema.hasOwnProperty('parentField')
        )
    )
)

export const DependentSelectControl = (props) => {
    const { path, schema, uischema, data, handleChange } = props
    const dynamicConfigs = useDynamicConfigs()
    const previewMode = usePreviewMode()
    const notify = useNotify()
    const formContext = useJsonForms()
    const [selectError, setSelectError] = useState('')
    const parentScope = uischema?.options?.parentScope || schema.parentField
    const dependentMappingScope =
        uischema?.options?.dependentMappingScope || schema.optionsMapPath
    // Get the parent field name from parentScope
    const parentField = parentScope.split('/').at(-1)

    // Check if this is being used as a cell renderer (in an array)
    const isCell = path && path.includes('.')

    const { MaterialEnumControl } = Unwrapped

    useEffect(() => {
        let errorString = ''

        if (!parentScope) {
            errorString +=
                'parentField is missing from schema and uischema options\n'
        }
        if (!dependentMappingScope) {
            errorString +=
                'optionsMapPath is missing from schema and uischema options\n'
        }

        setSelectError(errorString)
    }, [parentScope, dependentMappingScope])

    // Helper function to get the parent path when in an array
    const getParentValueFromPath = (currentPath, parentScope) => {
        if (isCell) {
            // Extract the array index part
            const pathParts = currentPath.split('.')
            const arrayIndex = pathParts.slice(0, -1).join('.') // Get everything except the last part

            // Construct the full path to the parent value
            const parentPath = `${arrayIndex}.${parentField}`

            const foundValue = get(formContext.core.data, parentPath, false)
            if (foundValue) {
                return foundValue
            }
        }

        // if we are not in an array, or if the parent path was not found in the array,
        // we look for the parent value in the top level of the data object
        return formContext.core.data?.[parentScope.split('/').at(-1)]
    }

    const parentValue = getParentValueFromPath(path, parentScope)
    const staticConfigs = formContext.core.schema.properties

    const selectList = useMemo(() => {
        // Get a user-friendly label for the parent field
        const getParentFieldLabel = () => {
            // Try to find the parent field's title in the schema
            const parentFieldSchema = get(
                formContext.core.schema,
                parentScope.replace('#/', '')
            )
            if (parentFieldSchema?.title) {
                return parentFieldSchema.title
            }

            // If in an array, check the parent object's properties
            if (path.includes('.')) {
                // Extract the array path from the current path
                const currentPathParts = path.split('.')
                const arrayPath = currentPathParts[0] // e.g., 'pokemon_list'
                const parentSchema = get(
                    formContext.core.schema,
                    `properties.${arrayPath}.items.properties.${parentField}`
                )
                if (parentSchema?.title) {
                    return parentSchema.title
                }
            }

            // Fall back to the field name, converting snake_case to Title Case
            return parentField
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
        }

        const isDynamic = dependentMappingScope.startsWith('#/definitions/')

        const pathArray = dependentMappingScope
            .replace(isDynamic ? '#/definitions/' : '#/properties/', '')
            .split('/')

        const targetValue = pathArray.reduce(
            (obj, key) => obj?.[key],
            isDynamic ? dynamicConfigs : staticConfigs
        )

        if (!targetValue && !previewMode) {
            notify(
                'Something is wrong with the configuration of your integration. ' +
                    'You might need to refresh the configs, or contact an administrator.'
            )
        }

        let selectContent = targetValue?.[parentValue]

        if (!Array.isArray(selectContent) || selectContent?.length < 1) {
            return [
                {
                    title: `Please select a ${getParentFieldLabel()}`,
                    const: 'Placeholder',
                },
            ]
        }

        switch (typeof selectContent[0]) {
            case 'object':
                break
            case 'string':
                // Convert enum to match oneOf format to standardize the component
                selectContent = targetValue?.[parentValue]?.map((value) => ({
                    title: value,
                    const: value,
                }))
                break
            default:
                selectContent = [{ title: 'Placeholder', const: 'Placeholder' }]
                break
        }

        return selectContent
    }, [
        dependentMappingScope,
        dynamicConfigs,
        staticConfigs,
        parentValue,
        parentField,
        parentScope,
        path,
        formContext.core.schema,
        previewMode,
    ])

    // Clear the component data when the dynamic key changes
    useEffect(() => {
        if (!selectList.find((o) => o?.const === data)) {
            handleChange(path, '')
        }
    }, [parentValue, selectList, data, handleChange, path])

    // Convert selectList to the format expected by MaterialEnumControl
    const options = selectList.map((item) => ({
        label: item.title,
        value: item.const,
    }))

    return selectError ? (
        <Box>
            <Box>{uischema.label}</Box>
            <Box sx={{ marginLeft: '20px', whiteSpace: 'pre-line' }}>
                {selectError}
            </Box>
        </Box>
    ) : (
        <Box
            sx={{
                '& .MuiFormControl-root': {
                    minWidth: isCell ? '100%' : '225px',
                    width: isCell ? '100%' : 'auto',
                },
            }}
        >
            <MaterialEnumControl
                {...props}
                uischema={
                    !uischema.options?.trim
                        ? { ...uischema, options: { trim: true } }
                        : uischema
                }
                options={options}
            />
        </Box>
    )
}

export default withJsonFormsControlProps(
    withTranslateProps(DependentSelectControl)
)
